import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Grid,
  Stack,
  Divider,
  Autocomplete,
} from "@mui/material";
import { Snackbar, Alert } from "@mui/material";
import { countries } from "../../constants/countrylist";
import {
  createNewPortfolioUser,
  PortfolioAssignDelegate,
} from "../../util/API/portfolio";

const AdminConsole = ({ onExit, userRole }) => {
  const [onboardData, setOnboardData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    contactNumber: "",
    areaCode: "",
    countryCode: "",
    gender: "",
    role: "Investor",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  let agentID = localStorage.getItem("authUser");

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setOnboardData((prev) => ({ ...prev, [name]: value }));
  };

  const clearFields = () => {
    setOnboardData({
      firstName: "",
      lastName: "",
      email: "",
      contactNumber: "",
      areaCode: "",
      countryCode: "",
      gender: "",
      role: "Investor",
    });
  };

  const handleOnboardSubmit = async () => {
    console.log("Onboarding User:", onboardData);
    let roleToBeSend = userRole === "admin" ? "investor" : "delegate";
    const roleLabel = userRole === "admin" ? "Investor" : "Delegate";
    const investorId = JSON.parse(sessionStorage.getItem("user"))?.userid || "";
    console.log(investorId);
    try {
      const response = await createNewPortfolioUser(
        onboardData?.email,
        onboardData?.firstName,
        onboardData?.lastName,
        onboardData?.countryCode,
        onboardData?.contactNumber,
        onboardData?.gender,
        agentID,
        2,
        roleToBeSend,
      );

      if (userRole !== "admin") {
        const delegateId = response?.payload?.userid;
        if (!delegateId) {
          console.error("delegate_id not found in response");
          return;
        }
        const assignResponse = await PortfolioAssignDelegate(
          "",
          investorId,
          delegateId,
        );
        console.log("AssignDelegate response:", assignResponse);
      }

      console.log("response=========", JSON.stringify(response));

      setSnackbar({
        open: true,
        message: `${onboardData.firstName} ${onboardData.lastName} has been successfully onboarded as a ${roleLabel}.`,
        severity: "success",
      });

      clearFields();
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Something went wrong. Please try again.",
        severity: "error",
      });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <Box className="admin-container">
      {/* Header Bar */}
      <Box className="header-card">
        <Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h5" fontWeight="bold">
              THE HOLT XCHANGE
            </Typography>
            <Typography
              variant="h6"
              sx={{ ml: 2, borderLeft: "1px solid #ccc", pl: 2 }}
            >
              Admin Console
            </Typography>
          </Box>
          <Typography variant="body2" color="textSecondary">
            Manage users, uploads, investor profiles, and permissions
          </Typography>
        </Box>
        <Button
          variant="outlined"
          onClick={onExit}
          sx={{ textTransform: "none", color: "#000", borderColor: "#000" }}
        >
          Exit Admin
        </Button>
      </Box>

      {/* Onboard User Section */}
      <Box className="dashboard-card" sx={{ mb: 3 }}>
        <Typography variant="h6" fontWeight="bold">
          Onboard New {userRole === "admin" ? `Investors` : `Delegate`}
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
          Enter the details below to create a new user profile and trigger the
          onboarding sequence.
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="First Name"
              name="firstName"
              size="small"
              value={onboardData.firstName}
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Last Name"
              name="lastName"
              size="small"
              value={onboardData.lastName}
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Email Address"
              name="email"
              type="email"
              size="small"
              value={onboardData.email}
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Contact Number"
              name="contactNumber"
              size="small"
              value={onboardData.contactNumber}
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Autocomplete
              options={countries.filter((c) => c.id !== "novalue")}
              getOptionLabel={(option) =>
                `${option.country} (${option.areaCode})`
              }
              value={
                countries.find((c) => c.flag === onboardData.countryCode) ||
                null
              }
              onChange={(event, newValue) => {
                setOnboardData((prev) => ({
                  ...prev,
                  areaCode: newValue ? newValue.areaCode : "",
                  countryCode: newValue ? newValue.flag : "",
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Country Code"
                  size="small"
                  placeholder="Search country..."
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <Select
                name="gender"
                value={onboardData.gender}
                onChange={handleInputChange}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  Select Gender
                </MenuItem>
                <MenuItem value="1">Male</MenuItem>
                <MenuItem value="2">Female</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ my: 1, borderStyle: "dashed" }} />
            <Button
              variant="contained"
              onClick={handleOnboardSubmit}
              sx={{ bgcolor: "#001529", textTransform: "none", mt: 1, px: 4 }}
            >
              Complete Onboarding
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Permissions Section */}
      {/* <Box className="dashboard-card" sx={{ mb: 3 }}>
        <Typography variant="h6" fontWeight="bold">
          Permissions
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Assign documents to investor or fund-wide scope. Manage viewing
          rights.
        </Typography>
        <Button variant="outlined" sx={{ textTransform: "none" }}>
          Open Permission Manager
        </Button>
      </Box> */}

      {/* Audit Log Section */}
      {/* <Box className="dashboard-card" marginBottom={8}>
        <Typography variant="h6" fontWeight="bold">
          Audit Log
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          View recent activity (uploads, downloads, access changes).
        </Typography>
        <Button variant="outlined" sx={{ textTransform: "none" }}>
          Export CSV
        </Button>
      </Box> */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminConsole;
